import type { ActivityEntry, BugStatus, SuggestionStatus } from '../types'

export const BUG_STATUS_LABEL: Record<BugStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  resolved: 'Resolved',
}

export const BUG_STATUS_CLASS: Record<BugStatus, string> = {
  todo: 'bg-muted text-muted-foreground',
  in_progress: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  resolved: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
}

export const SUGGESTION_STATUS_LABEL: Record<SuggestionStatus, string> = {
  new: 'New',
  under_review: 'Under review',
  planned: 'Planned',
  done: 'Done',
  declined: 'Declined',
}

export const SUGGESTION_STATUS_CLASS: Record<SuggestionStatus, string> = {
  new: 'bg-muted text-muted-foreground',
  under_review: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  planned: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  done: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  declined: 'bg-red-500/15 text-red-700 dark:text-red-300',
}

export function suggestionStatusLabel(status: string): string {
  return SUGGESTION_STATUS_LABEL[status as SuggestionStatus] ?? status
}

export const SEVERITY_CLASS: Record<string, string> = {
  Low: 'bg-muted text-muted-foreground',
  Medium: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  High: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  Blocking: 'bg-red-500/15 text-red-700 dark:text-red-300',
}

export const ACTION_LABEL: Record<string, string> = {
  status_changed: 'Status',
  assignee_changed: 'Assignee',
  module_corrected: 'Module',
  severity_changed: 'Severity',
  note_updated: 'Internal notes',
}

export function bugStatusLabel(status: string): string {
  return BUG_STATUS_LABEL[status as BugStatus] ?? status
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Activity row text: "Status: To Do → In Progress", "Assignee: — → dev-a". */
export function describeActivity(
  action: string,
  from: string | null,
  to: string | null,
  statusLabel: (s: string) => string = bugStatusLabel,
): string {
  const label = ACTION_LABEL[action] ?? action
  if (action === 'note_updated') return to ? `${label} updated` : `${label} cleared`
  const show = (v: string | null) => (v == null ? '—' : action === 'status_changed' ? statusLabel(v) : v)
  return `${label}: ${show(from)} → ${show(to)}`
}

export interface ActivityEvent {
  key: string
  actor: string
  at: string | null
  changes: string[]
}

/**
 * Collapse consecutive entries by the same actor at the same timestamp (one
 * save that touched several fields) into one event, newest event first.
 * Within an event the changes keep write order, so "Assignee" precedes the
 * "Status" it triggered.
 */
export function groupActivity(entries: ActivityEntry[], statusLabel: (s: string) => string = bugStatusLabel): ActivityEvent[] {
  const events: ActivityEvent[] = []
  for (const e of entries) {
    const last = events[events.length - 1]
    const text = describeActivity(e.action, e.from_value, e.to_value, statusLabel)
    if (last && last.actor === e.actor && last.at === e.created_at) {
      last.changes.push(text)
    } else {
      events.push({ key: String(e.id), actor: e.actor, at: e.created_at, changes: [text] })
    }
  }
  return events.reverse()
}
